import { Injectable, ComponentRef, OnDestroy } from "@angular/core";
import { Overlay, OverlayRef, OverlayPositionBuilder, OverlayConfig } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { BreakpointObserver } from "@angular/cdk/layout";
import { DsNotificationComponent } from "../components/ds-notification/ds-notification.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DsNotificationConfig } from "../components/interfaces/notification.interface";

@Injectable({
  providedIn: "root",
})
export class DsNotificationService implements OnDestroy {
  private overlayRef: OverlayRef | null = null;
  private notificationRef: ComponentRef<DsNotificationComponent> | null = null;
  private destroy$ = new Subject<void>();
  private isMobile = false;
  private autoCloseTimeout: any = null;
  private openDelayTimeout: any = null;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private breakpointObserver: BreakpointObserver
  ) {
    const breakpoints_mobile = "(max-width: 767px)";
    this.breakpointObserver
      .observe(breakpoints_mobile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.isMobile = result.matches;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }

    if (this.openDelayTimeout) {
      clearTimeout(this.openDelayTimeout);
      this.openDelayTimeout = null;
    }

    this.close();
  }

  open(config: DsNotificationConfig): void {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }

    if (this.openDelayTimeout) {
      clearTimeout(this.openDelayTimeout);
      this.openDelayTimeout = null;
    }

    if (this.overlayRef) {
      this.close();
      this.openDelayTimeout = setTimeout(() => {
        this.createNotification(config);
        this.openDelayTimeout = null;
      }, 250);
    } else {
      this.createNotification(config);
    }
  }

  createNotification(config: DsNotificationConfig): void {
    config.time = Math.max(config.time ?? 5000, 4000);

    const overlayConfig: OverlayConfig = {
      positionStrategy: this.createPositionStrategy(config),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      panelClass: "ds-notification-overlay",
    };

    this.overlayRef = this.overlay.create(overlayConfig);

    const portal = new ComponentPortal(DsNotificationComponent);
    this.notificationRef = this.overlayRef.attach(portal);

    if (this.notificationRef.instance) {
      const topOffset = config.topPosition ?? "16px";
      this.notificationRef.instance.type = config.type ?? "neutral";
      this.notificationRef.instance.actionLabel = config.actionLabel!;
      this.notificationRef.instance.title = config.title!;
      this.notificationRef.instance.message = config.message!;
      this.notificationRef.instance.animationState = "visible";
      this.notificationRef.instance.callAction = config.callAction;
      this.notificationRef.instance.topPosition = topOffset;
      this.notificationRef.instance.showClose = config.showClose;
    }

    if (config.time! > 0 && config.type !== "warning" && config.type !== "danger") {
      this.autoCloseTimeout = setTimeout(() => {
        this.close();
      }, config.time);
    }
  }

  close(): void {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }

    if (this.overlayRef && this.notificationRef) {
      this.notificationRef.instance.animationState = "hidden";
      setTimeout(() => {
        if (this.overlayRef) {
          this.overlayRef.detach();
          this.overlayRef.dispose();
          this.overlayRef = null;
          this.notificationRef = null;
        }
      }, 150);
    }
  }

  private createPositionStrategy(config?: DsNotificationConfig) {
    const topOffset = config?.topPosition ?? "16px";
    if (this.isMobile) {
      return this.overlayPositionBuilder.global().top(topOffset).right("16px").left("16px").centerHorizontally();
    } else {
      return this.overlayPositionBuilder.global().bottom("48px").right("40px");
    }
  }
}
